import { useState, useMemo } from 'react';
import { Language } from '../../types/translation-types';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { Button } from '@/core/components/ui/button';
import { Badge } from '@/core/components/ui/badge';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Edit2, Trash2, AlertTriangle, GripVertical } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { parseCompoundLanguageCode } from '../../utils/language-code';

interface LanguageGridProps {
  languages: Language[];
  isLoading: boolean;
  error: string | null;
  onEdit: (language: Language) => void;
  onDelete: (language: Language) => void;
  onReorder: (languages: Language[]) => void;
  onRetry: () => void;
}

// Sortable Language Row
function SortableLanguageRow({
  language,
  onEdit,
  onDelete,
}: {
  language: Language;
  onEdit: (language: Language) => void;
  onDelete: (language: Language) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: language.languageId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        GRID_STYLES.row.default,
        GRID_STYLES.row.hover,
        `border-b ${GRID_STYLES.border}`,
        'transition-colors',
        isDragging && 'z-50'
      )}
    >
      <td className={GRID_STYLES.cell.base}>
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-move touch-none"
            title="Drag to reorder"
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
          <span className="font-mono text-sm">{language.languageCode}</span>
        </div>
      </td>
      <td className={GRID_STYLES.cell.base}>
        {parseCompoundLanguageCode(language.languageCode).operatorName ?? ''}
      </td>
      <td className={GRID_STYLES.cell.base}>{language.languageName}</td>
      <td className={cn(GRID_STYLES.cell.base, language.isRtl ? 'text-right' : '')}>
        {language.nativeName}
      </td>
      <td className={GRID_STYLES.cell.base}>
        {language.isRtl ? (
          <Badge variant="secondary" className="text-xs">
            RTL
          </Badge>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className={cn(GRID_STYLES.cell.base, 'text-center')}>{language.sortOrder}</td>
      <td className={GRID_STYLES.cell.base}>
        <Badge
          variant={language.isActive ? 'default' : 'secondary'}
          className={cn(
            'text-xs',
            language.isActive
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-gray-100 text-gray-600'
          )}
        >
          {language.isActive ? 'Active' : 'Inactive'}
        </Badge>
      </td>
      <td className={GRID_STYLES.cell.base}>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(language)}
            className="h-8 w-8 p-0"
            title="Edit language"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(language)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Delete language"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function LanguageGrid({
  languages,
  isLoading,
  error,
  onEdit,
  onDelete,
  onReorder,
  onRetry,
}: LanguageGridProps) {
  const [activeId, setActiveId] = useState<number | null>(null);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px drag distance before activating
      },
    })
  );

  // Sort languages by sortOrder
  const sortedLanguages = useMemo(() => {
    return [...languages].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [languages]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = sortedLanguages.findIndex((lang) => lang.languageId === active.id);
    const newIndex = sortedLanguages.findIndex((lang) => lang.languageId === over.id);

    if (oldIndex !== newIndex) {
      const reordered = arrayMove(sortedLanguages, oldIndex, newIndex);
      onReorder(reordered);
    }
  };

  const activeDragItem = useMemo(() => {
    if (!activeId) return null;
    return sortedLanguages.find((lang) => lang.languageId === activeId);
  }, [activeId, sortedLanguages]);
  // Loading state
  if (isLoading) {
    return (
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Language Code</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Operator</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Language Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Native Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-20')}>RTL</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Sort Order</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Status</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className={cn(GRID_STYLES.row.default, `border-b ${GRID_STYLES.border}`)}>
                <td className={GRID_STYLES.cell.base}>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-16" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-24" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-24" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-10" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-10" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-6 w-16" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-8 w-20" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-8 text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-4 text-lg font-medium text-red-800">Failed to load languages</h3>
        <p className="mt-2 text-sm text-red-600">{error}</p>
        <Button onClick={onRetry} variant="outline" className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty state
  if (languages.length === 0) {
    return (
      <div className="rounded-md border border-[#E8E8E8] bg-gray-50 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
          <span className="text-2xl">🌐</span>
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No languages found</h3>
        <p className="mt-2 text-sm text-gray-500">
          Get started by creating a new language.
        </p>
      </div>
    );
  }

  // Data state
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Language Code</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Operator</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Language Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Native Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-20')}>RTL</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Sort Order</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Status</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <SortableContext
              items={sortedLanguages.map((lang) => lang.languageId)}
              strategy={verticalListSortingStrategy}
            >
              {sortedLanguages.map((language) => (
                <SortableLanguageRow
                  key={language.languageId}
                  language={language}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeDragItem ? (
          <div className="bg-white border rounded shadow-lg p-3">
            <table className="w-full">
              <tbody>
                <tr className="flex items-center gap-4">
                  <td>
                    <GripVertical className="h-4 w-4 text-gray-400" />
                  </td>
                  <td className="font-mono text-sm">{activeDragItem.languageCode}</td>
                  <td className="font-medium">{activeDragItem.languageName}</td>
                  <td>{activeDragItem.nativeName}</td>
                  {activeDragItem.isRtl && (
                    <td>
                      <Badge variant="secondary" className="text-xs">
                        RTL
                      </Badge>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
